export type AuthGuestLinksMode = 'login_signup' | 'login_only_mobile' | 'login_only_all';
export type AuthCustomerNameMode = 'full' | 'first_name';

export interface AuthDisplayIdentity {
  login?: string;
  name?: string;
  firstname?: string;
  lastname?: string;
  customer_login?: string;
  customer_name?: string;
  customer_firstname?: string;
  customer_lastname?: string;
  first_name?: string;
  last_name?: string;
}

export interface AuthDisplaySettings {
  guestLinksMode: AuthGuestLinksMode;
  mobileIconOnly: boolean;
  customerNameMode: AuthCustomerNameMode;
}

export const DEFAULT_AUTH_DISPLAY_SETTINGS: AuthDisplaySettings = {
  guestLinksMode: 'login_signup',
  mobileIconOnly: false,
  customerNameMode: 'full',
};

export function authDisplaySettingsFromDataset(
  dataset: Record<string, string | undefined>,
): AuthDisplaySettings {
  const value = dataset.swAuthGuestLinksMode;
  const guestLinksMode: AuthGuestLinksMode = value === 'login_only_mobile' || value === 'login_only_all'
    ? value
    : 'login_signup';

  return {
    guestLinksMode,
    mobileIconOnly: dataset.swAuthMobileIconOnly === '1',
    customerNameMode: dataset.swAuthCustomerNameMode === 'first_name' ? 'first_name' : 'full',
  };
}

export function formatAuthDisplayLabel(
  auth: AuthDisplayIdentity | null | undefined,
  mode: AuthCustomerNameMode,
): string | undefined {
  const name = auth?.name ?? auth?.customer_name;
  if (name != null) {
    const normalized = name.trim().replace(/\s+/g, ' ');
    if (mode === 'first_name') {
      const firstName = auth?.firstname ?? auth?.customer_firstname ?? auth?.first_name;
      const normalizedFirstName = firstName?.trim().replace(/\s+/g, ' ');
      return normalizedFirstName || normalized.split(' ')[0];
    }
    return normalized;
  }
  return auth?.login ?? auth?.customer_login;
}

export function normalizeAuthIdentity(raw: Record<string, unknown> | null | undefined) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const login = String(raw.customer_login ?? raw.login ?? '').trim();
  const name = String(raw.customer_name ?? raw.name ?? '').trim();
  const firstname = String(raw.customer_firstname ?? raw.firstname ?? raw.first_name ?? '').trim();
  const lastname = String(raw.customer_lastname ?? raw.lastname ?? raw.last_name ?? '').trim();
  const customerUid = String(raw.customer_uid ?? raw.uid ?? '').trim();
  const customerEmail = String(raw.customer_email ?? raw.email ?? '').trim();

  if (!login && !customerUid && !customerEmail) return null;

  return {
    login: login || undefined,
    name: name || undefined,
    firstname: firstname || undefined,
    lastname: lastname || undefined,
    suc_count: raw.suc_count as number | undefined,
    bonus_account: raw.bonus_account as number | undefined,
    customer_uid: customerUid || undefined,
    customer_email: customerEmail || undefined,
  };
}

export function authDisplaySettingsFromElement(element: Element | null): AuthDisplaySettings {
  return authDisplaySettingsFromDataset((element as HTMLElement | null)?.dataset ?? {});
}
